import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import DashboardLayout from '../UI/Layout/DashboardLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSpinner, faChartBar, faMobileAlt,
    faClock, faCheckCircle, faInbox, faSearch, faSync,
    faUser, faPhone, faEnvelope, faKey, faDollarSign,
    faHashtag, faGlobe, faTag,
} from '@fortawesome/free-solid-svg-icons';
import { NODE_HOST_URL_v1 } from './api/Api';
import Axios from '../../axios/Axios';

/* ── helpers ── */
const formatDate = (str) => {
    if (!str) return '—';
    const d = new Date(str);
    if (isNaN(d)) return str;
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const formatPhone = (num) => {
    if (!num) return '—';
    const s = String(num);
    if (s.length >= 12) return `+${s.slice(0, 2)} ${s.slice(2, 7)} ${s.slice(7)}`;
    if (s.length === 10) return `+91 ${s.slice(0, 5)} ${s.slice(5)}`;
    return s;
};

const FIELD_ICON_MAP = {
    email: faEnvelope,
    password: faKey,
    passcode: faKey,
    mobile: faPhone,
    phone: faPhone,
    amount: faDollarSign,
    name: faUser,
    flow_token: faTag,
};

const getFieldIcon = (key) => {
    const k = key.toLowerCase();
    for (const [pattern, icon] of Object.entries(FIELD_ICON_MAP)) {
        if (k.includes(pattern)) return icon;
    }
    return faHashtag;
};

const isSecretField = (key) => {
    const k = key.toLowerCase();
    return k.includes('password') || k.includes('passcode') || k.includes('token');
};

/* ── sub-components ── */

const Pill = ({ children, color = '#2563eb', bg = '#eff6ff', border = '#bfdbfe' }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '2px 10px', borderRadius: '20px', fontSize: '11px',
        fontWeight: '600', border: `1px solid ${border}`,
        background: bg, color,
    }}>
        {children}
    </span>
);

const SubmitViaBadge = ({ via }) => {
    const map = {
        API: { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
        CAMPAIGN: { color: '#0369a1', bg: '#e0f2fe', border: '#bae6fd' },
        MANUAL: { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
    };
    const s = map[String(via).toUpperCase()] || { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' };
    return <Pill color={s.color} bg={s.bg} border={s.border}>{via || 'Unknown'}</Pill>;
};

const SecretValue = ({ value }) => {
    const [show, setShow] = useState(false);
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontFamily: 'monospace', letterSpacing: show ? 'normal' : '2px', color: '#1e293b', fontSize: '13px' }}>
                {show ? value : '•'.repeat(Math.min(String(value).length, 8))}
            </span>
            <button
                onClick={() => setShow(v => !v)}
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: '#94a3b8', fontSize: '11px', fontWeight: '600' }}
            >
                {show ? 'hide' : 'show'}
            </button>
        </span>
    );
};

const ResponseCard = ({ entry, index }) => {
    const [expanded, setExpanded] = useState(true);
    const rawFields = entry.flow_json || entry.flow_response || {};
    const fields = Object.entries(rawFields);

    return (
        <div style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
            transition: 'box-shadow 0.2s',
        }}>
            {/* card header */}
            <div
                onClick={() => setExpanded(v => !v)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 18px', cursor: 'pointer',
                    background: expanded ? '#f8fafc' : '#fff',
                    borderBottom: expanded ? '1px solid #e8eef5' : 'none',
                    transition: 'background 0.15s',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <div style={{
                        width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #25d366, #128c7e)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: '700', fontSize: '14px',
                    }}>
                        {index + 1}
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#25d366', fontSize: '12px' }} />
                            {formatPhone(entry.mobile)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <FontAwesomeIcon icon={faClock} style={{ fontSize: '10px' }} />
                            {formatDate(entry.dlr_response_time || entry.created)}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <SubmitViaBadge via={entry.submit_via} />
                    {entry.template_id && (
                        <Pill color="#0369a1" bg="#e0f2fe" border="#bae6fd">
                            Tpl #{entry.template_id}
                        </Pill>
                    )}
                    <span style={{ color: '#94a3b8', fontSize: '13px', marginLeft: '4px' }}>
                        {expanded ? '▲' : '▼'}
                    </span>
                </div>
            </div>

            {/* card body */}
            {expanded && (
                <div style={{ padding: '16px 18px' }}>
                    {/* meta row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '10px',
                        marginBottom: '16px',
                        padding: '12px 14px',
                        background: '#f8fafc',
                        borderRadius: '10px',
                        border: '1px solid #e8eef5',
                    }}>
                        {entry.camp_id && <MetaItem label="Campaign ID" value={entry.camp_id} mono />}
                        <MetaItem label="Submitted" value={formatDate(entry.created)} />
                        {entry.dlr_response_time && entry.dlr_response_time !== entry.created && (
                            <MetaItem label="Responded" value={formatDate(entry.dlr_response_time)} />
                        )}
                    </div>

                    {/* form response */}
                    {fields.length > 0 && (
                        <>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>
                                Form Response · {fields.length} field{fields.length !== 1 ? 's' : ''}
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                gap: '8px',
                            }}>
                                {fields.map(([k, v]) => (
                                    <FormField key={k} fieldKey={k} value={v} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const MetaItem = ({ label, value, mono }) => (
    <div>
        <div style={{ fontSize: '10px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '13px', color: '#1e293b', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value || '—'}</div>
    </div>
);

const FormField = ({ fieldKey, value }) => {
    const icon = getFieldIcon(fieldKey);
    const secret = isSecretField(fieldKey);
    const label = fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    return (
        <div style={{
            background: '#fff',
            border: '1px solid #e8eef5',
            borderRadius: '10px',
            padding: '10px 14px',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
        }}>
            <div style={{
                width: '30px', height: '30px', borderRadius: '8px', flexShrink: 0,
                background: '#f0f7ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <FontAwesomeIcon icon={icon} style={{ fontSize: '12px', color: '#2563eb' }} />
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500', marginBottom: '3px' }}>{label}</div>
                {secret
                    ? <SecretValue value={value} />
                    : <div style={{ fontSize: '13px', color: '#1e293b', wordBreak: 'break-all', fontWeight: '500' }}>{String(value)}</div>
                }
            </div>
        </div>
    );
};

/* ── main component ── */
const WhatsappFlowTracker = () => {
    const { id: flowId } = useParams();
    const history = useHistory();
    const lStorage = JSON.parse(localStorage.getItem('login'));
    const userId = lStorage?.user?.userId;
    const userToken = lStorage?.token;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await Axios.post(`${NODE_HOST_URL_v1}/FlowResponse`, {
                user_id: String(userId),
                token: userToken,
                method: 'getFlow',
                flowId: flowId,
            });
            if (res.data?.success) {
                setData(Array.isArray(res.data.data) ? res.data.data : []);
            } else {
                setError(res.data?.message || 'Failed to fetch response data.');
                setData([]);
            }
        } catch (e) {
            setError('Network error. Please try again.');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (flowId) fetchData();
    }, [flowId]);

    const filtered = data.filter(entry => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const mobile = String(entry.mobile || '').toLowerCase();
        const brandNum = String(entry.brand_number || '').toLowerCase();
        const responseStr = JSON.stringify(entry.flow_json || entry.flow_response || {}).toLowerCase();
        return mobile.includes(q) || brandNum.includes(q) || responseStr.includes(q);
    });

    return (
        <DashboardLayout>
            <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '24px 20px' }}>
                <div style={{ maxWidth: '960px', margin: '0 auto' }}>

                    {/* page header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => history.goBack()}
                            style={{
                                width: '36px', height: '36px', borderRadius: '10px',
                                border: '1px solid #e2e8f0', background: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#475569', flexShrink: 0,
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '13px' }} />
                        </button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <div style={{
                                    width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                                    background: 'linear-gradient(135deg, #25d366, #128c7e)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <FontAwesomeIcon icon={faChartBar} style={{ color: '#fff', fontSize: '16px' }} />
                                </div>
                                <div>
                                    <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
                                        Flow Response Tracker
                                    </h1>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '1px', fontFamily: 'monospace' }}>
                                        Flow ID: {flowId}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '7px',
                                padding: '8px 16px', borderRadius: '10px',
                                border: '1px solid #e2e8f0', background: '#fff',
                                color: '#475569', fontSize: '13px', fontWeight: '600',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                opacity: loading ? 0.7 : 1,
                            }}
                        >
                            <FontAwesomeIcon icon={faSync} style={{ fontSize: '11px', animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                            Refresh
                        </button>
                    </div>

                    {/* common fields banner */}
                    {!loading && data.length > 0 && (
                        <div style={{
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '14px 18px',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '24px',
                        }}>
                            <div>
                                <div style={{ fontSize: '10px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Brand Number</div>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#25d366', fontSize: '12px' }} />
                                    {formatPhone(data[0]?.brand_number)}
                                </div>
                            </div>
                            <div style={{ width: '1px', height: '36px', background: '#e2e8f0', flexShrink: 0 }} />
                            <div>
                                <div style={{ fontSize: '10px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Flow ID</div>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', fontFamily: 'monospace' }}>
                                    {data[0]?.flow_id || flowId}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* stats bar */}
                    {!loading && data.length > 0 && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '12px', marginBottom: '20px',
                        }}>
                            <StatCard icon={faInbox} label="Total Responses" value={data.length} color="#2563eb" />
                            <StatCard icon={faGlobe} label="Via API" value={data.filter(d => d.submit_via === 'API').length} color="#7c3aed" />
                            <StatCard icon={faCheckCircle} label="Showing" value={filtered.length} color="#16a34a" />
                        </div>
                    )}

                    {/* search */}
                    {data.length > 0 && (
                        <div style={{ position: 'relative', marginBottom: '20px' }}>
                            <FontAwesomeIcon icon={faSearch} style={{
                                position: 'absolute', left: '14px', top: '50%',
                                transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '13px',
                            }} />
                            <input
                                type="text"
                                placeholder="Search by mobile, brand number, or form field value…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{
                                    width: '100%', padding: '10px 14px 10px 38px',
                                    border: '1px solid #e2e8f0', borderRadius: '10px',
                                    fontSize: '13px', background: '#fff', color: '#1e293b',
                                    outline: 'none', boxSizing: 'border-box',
                                }}
                            />
                        </div>
                    )}

                    {/* content */}
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: '14px' }}>
                            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '28px', color: '#25d366' }} />
                            <span style={{ color: '#64748b', fontSize: '14px' }}>Loading flow responses…</span>
                        </div>
                    ) : error ? (
                        <div style={{
                            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px',
                            padding: '20px 24px', color: '#dc2626', fontSize: '14px',
                            display: 'flex', alignItems: 'center', gap: '10px',
                        }}>
                            <span style={{ fontSize: '18px' }}>⚠</span> {error}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div style={{
                            background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px',
                            padding: '60px 24px', textAlign: 'center',
                        }}>
                            <div style={{ fontSize: '40px', marginBottom: '14px' }}>📭</div>
                            <div style={{ fontWeight: '600', fontSize: '16px', color: '#1e293b', marginBottom: '6px' }}>
                                {search ? 'No matching responses' : 'No responses yet'}
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>
                                {search ? 'Try adjusting your search query.' : 'Responses will appear here once users submit the flow.'}
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {filtered.map((entry, i) => (
                                <ResponseCard key={entry._id || i} entry={entry} index={i} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </DashboardLayout>
    );
};

const StatCard = ({ icon, label, value, color }) => (
    <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
        padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px',
    }}>
        <div style={{
            width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
            background: `${color}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <FontAwesomeIcon icon={icon} style={{ color, fontSize: '15px' }} />
        </div>
        <div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>{label}</div>
        </div>
    </div>
);

export default WhatsappFlowTracker;
